import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import {
  PaginationType,
  ResetApiState,
  ResetPaginationState,
  ResponseWrapper,
} from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import {
  CancellationPolicy,
  CancellationPolicyStateTypes,
} from "./CancellationPolicy.util";

const name = "cancellation-policy";
const extraActions = createExtraActions();

const CancellationPolicyInitialState: CancellationPolicyStateTypes = {
  createCancellationPolicy: ResetApiState(null),
  resetCreateCancellationPolicy: ResetApiState(null),
  editCancellationPolicy: ResetApiState(null),
  resetEditCancellationPolicy: ResetApiState(null),
  deleteCancellationPolicy: ResetApiState(null),
  resetDeleteCancellationPolicy: ResetApiState(null),
  cancellationPolicys: ResetApiState([]),
  pagedCancellationPolicys: ResetApiState(ResetPaginationState()),
  cancellationPolicy: ResetApiState(null),
  resetCancellationPolicy: ResetApiState(null),
  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const createCancellationPolicy = createAsyncThunk<
    ResponseWrapper<CancellationPolicy>,
    any
  >(`/create-${name}`, async (data: any, thunkApi) =>
    axios
      .post(`/${name}`, data)
      .then((response) => {
        if (response.data.success) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCreateCancellationPolicy = createAction(`/reset-create-${name}`);

  const editCancellationPolicy = createAsyncThunk<
    ResponseWrapper<CancellationPolicy>,
    any
  >(`/edit-${name}`, async ({ data, id }: any, thunkApi) =>
    axios
      .put(`/${name}/${id}`, data)
      .then((response) => {
        if (response.data.success) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetEditCancellationPolicy = createAction(`/reset-edit-${name}`);

  const deleteCancellationPolicy = createAsyncThunk<number, any>(
    `/delete-${name}`,
    async (id: any, thunkApi) =>
      axios
        .delete(`${name}/${id}`)
        .then((response) => {
          if (response.data.success) {
            return id;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error?.response?.data);
        })
  );

  const resetDeleteCancellationPolicy = createAction(`/reset-delete-${name}`);

  const getCancellationPolicys = createAsyncThunk<CancellationPolicy[], any>(
    `/get-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getPagedCancellationPolicys = createAsyncThunk<
    PaginationType<CancellationPolicy[]>,
    any
  >(`/get-paged-${name}s`, async (query: any, thunkApi) => {
    return axios
      .get(`/${name}/paged${queryStringGenerator(query)}`)
      .then((response) => {
        if (response.data) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error: AxiosError) => {
        return thunkApi.rejectWithValue(error.message);
      });
  });

  const getCancellationPolicy = createAsyncThunk<CancellationPolicy, any>(
    `/get-${name}`,
    async (id: any, thunkApi) =>
      axios
        .get(`${name}/${id}`)
        .then((response) => response.data)
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCancellationPolicy = createAction(`/reset-get-${name}`);

  const resetStates = createAction(`/reset-states`);

  return {
    createCancellationPolicy,
    resetCreateCancellationPolicy,
    editCancellationPolicy,
    resetEditCancellationPolicy,
    deleteCancellationPolicy,
    resetDeleteCancellationPolicy,
    getCancellationPolicys,
    getPagedCancellationPolicys,
    getCancellationPolicy,
    resetCancellationPolicy,
    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<CancellationPolicyStateTypes>
) {
  return {
    ...createCancellationPolicyReducer(),
    ...resetCreateCancellationPolicy(),
    ...editCancellationPolicyReducer(),
    ...resetEditCancellationPolicy(),
    ...deleteCancellationPolicyReducer(),
    ...resetDeleteCancellationPolicy(),
    ...cancellationPolicysReducer(),
    ...pagedCancellationPolicysReducer(),
    ...cancellationPolicyReducer(),
    ...resetCancellationPolicy(),
    ...resetStatesReducer(),
  };

  function createCancellationPolicyReducer() {
    return {
      ...builder.addCase(
        extraActions.createCancellationPolicy.pending,
        (state, action) => {
          state.createCancellationPolicy = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createCancellationPolicy.fulfilled,
        (state, action) => {
          state.createCancellationPolicy = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createCancellationPolicy.rejected,
        (state, action) => {
          state.createCancellationPolicy = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetCreateCancellationPolicy() {
    return {
      ...builder.addCase(
        extraActions.resetCreateCancellationPolicy,
        (state, action) => {
          state.resetCreateCancellationPolicy = ResetApiState(true);
          state.createCancellationPolicy = ResetApiState(null);
        }
      ),
    };
  }

  function editCancellationPolicyReducer() {
    return {
      ...builder.addCase(
        extraActions.editCancellationPolicy.pending,
        (state, action) => {
          state.editCancellationPolicy = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.editCancellationPolicy.fulfilled,
        (state, action) => {
          state.editCancellationPolicy = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.editCancellationPolicy.rejected,
        (state, action) => {
          state.editCancellationPolicy = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetEditCancellationPolicy() {
    return {
      ...builder.addCase(
        extraActions.resetEditCancellationPolicy,
        (state, action) => {
          state.resetEditCancellationPolicy = ResetApiState(true);
          state.editCancellationPolicy = ResetApiState(null);
        }
      ),
    };
  }

  function deleteCancellationPolicyReducer() {
    return {
      ...builder.addCase(
        extraActions.deleteCancellationPolicy.pending,
        (state, action) => {
          state.deleteCancellationPolicy = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteCancellationPolicy.fulfilled,
        (state, action) => {
          state.deleteCancellationPolicy = {
            loading: false,
            payload: true,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteCancellationPolicy.rejected,
        (state, action) => {
          state.deleteCancellationPolicy = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDeleteCancellationPolicy() {
    return {
      ...builder.addCase(
        extraActions.resetDeleteCancellationPolicy,
        (state, action) => {
          state.resetDeleteCancellationPolicy = ResetApiState(true);
          state.deleteCancellationPolicy = ResetApiState(null);
        }
      ),
    };
  }

  function cancellationPolicysReducer() {
    return {
      ...builder.addCase(
        extraActions.getCancellationPolicys.pending,
        (state, action) => {
          state.cancellationPolicys = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getCancellationPolicys.fulfilled,
        (state, action) => {
          state.cancellationPolicys = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getCancellationPolicys.rejected,
        (state, action) => {
          state.cancellationPolicys = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function pagedCancellationPolicysReducer() {
    return {
      ...builder.addCase(
        extraActions.getPagedCancellationPolicys.pending,
        (state, action) => {
          state.pagedCancellationPolicys = {
            loading: true,
            payload: ResetPaginationState(),
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedCancellationPolicys.fulfilled,
        (state, action) => {
          state.pagedCancellationPolicys = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedCancellationPolicys.rejected,
        (state, action) => {
          state.pagedCancellationPolicys = {
            loading: false,
            payload: ResetPaginationState(),
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function cancellationPolicyReducer() {
    return {
      ...builder.addCase(
        extraActions.getCancellationPolicy.pending,
        (state, action) => {
          state.cancellationPolicy = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getCancellationPolicy.fulfilled,
        (state, action) => {
          state.cancellationPolicy = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getCancellationPolicy.rejected,
        (state, action) => {
          state.cancellationPolicy = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetCancellationPolicy() {
    return {
      ...builder.addCase(
        extraActions.resetCancellationPolicy,
        (state, action) => {
          state.resetCancellationPolicy = ResetApiState(true);
          state.cancellationPolicy = ResetApiState(null);
        }
      ),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.createCancellationPolicy = ResetApiState(null);
        state.resetCreateCancellationPolicy = ResetApiState(null);
        state.editCancellationPolicy = ResetApiState(null);
        state.resetEditCancellationPolicy = ResetApiState(null);
        state.deleteCancellationPolicy = ResetApiState(null);
        state.resetDeleteCancellationPolicy = ResetApiState(null);
        state.cancellationPolicys = ResetApiState([]);
        state.cancellationPolicy = ResetApiState(null);
        state.resetCancellationPolicy = ResetApiState(null);
        state.resetCancellationPolicy = ResetApiState(null);
      }),
    };
  }
}

const CancellationPolicySlice = createSlice({
  name,
  initialState: CancellationPolicyInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const CancellationPolicyActions = {
  ...CancellationPolicySlice.actions,
  ...extraActions,
};
export const CancellationPolicyReducer = CancellationPolicySlice.reducer;
